import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Product } from '../api/products';
import { formatPrice } from '../utils/formatPrice';
import { useCart } from '../context/CartContext';
import { getImageUrl } from '../utils/image';

interface ProductCardProps {
    product: Product;
    onQuickOrder: (product: Product) => void;
    isFocused?: boolean;
    viewMode?: 'grid' | 'list';
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onQuickOrder, isFocused = false, viewMode = 'grid' }) => {
    const { t, i18n } = useTranslation();
    const { addToCart } = useCart();
    const isRTL = i18n.dir() === 'rtl';
    const [isHovered, setIsHovered] = useState(false);

    // Unified active state: either hovered by mouse or focused by auto-sequence
    const isActive = isHovered || isFocused;

    const title = isRTL ? product.title_ar : product.title_fr;
    const price = product.price;
    const discountPrice = product.discount_price;
    const stock = product.stock || 0;
    const isLowStock = stock > 0 && stock < 10;

    // Defensive parsing: handle when badges come as strings instead of arrays
    const parseBadges = (badges: string[] | string | undefined): string[] => {
        if (!badges) return [];
        if (Array.isArray(badges)) return badges;
        if (typeof badges === 'string' && badges.trim()) {
            try {
                return JSON.parse(badges);
            } catch {
                return badges.split(',').map(b => b.trim()).filter(Boolean);
            }
        }
        return [];
    };

    const badgesFr = parseBadges(product.badges);
    const badgesAr = parseBadges(product.badges_ar);

    // Logic: If RTL and Arabic badges exist, show them. Otherwise fallback to French badges.
    const badges = (isRTL && badgesAr.length > 0) ? badgesAr : badgesFr;

    const isList = viewMode === 'list';

    // SOLD OUT Logic
    const isSoldOut = stock <= 0;

    return (
        <motion.div
            className={`group relative bg-white rounded-3xl transition-all duration-500 overflow-hidden border border-neutral-100 flex ${isList ? 'flex-row h-40 md:h-[280px] items-stretch' : 'flex-col h-full'} ${isActive && !isSoldOut ? 'shadow-2xl' : 'shadow-card'}`} // Disable shadow uplift if sold out
            onHoverStart={() => !isSoldOut && setIsHovered(true)} // Disable hover effect if sold out
            onHoverEnd={() => setIsHovered(false)}
            animate={{ y: isActive && !isSoldOut ? -10 : 0 }} // Disable lift animation if sold out
        >
            {/* Image Container */}
            <div className={`relative overflow-hidden bg-neutral-50 ${isList ? 'w-32 md:w-[280px] shrink-0' : 'aspect-[4/5] w-full'}`}>
                <Link to={`/product/${product.slug}`} className={isSoldOut ? 'cursor-default pointer-events-none' : ''}>
                    <img
                        src={getImageUrl(product.images[0])}
                        alt={title}
                        className={`w-full h-full object-cover transform transition-transform duration-700 ease-out ${isActive && !isSoldOut ? 'scale-110' : ''} ${isSoldOut ? 'grayscale-[50%] opacity-80' : ''}`}
                        loading="lazy"
                    />
                </Link>

                {/* SOLD OUT Overlay */}
                {isSoldOut && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/10 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-neutral-900/90 backdrop-blur-md text-white px-6 py-3 rounded-xl shadow-2xl border border-white/20 transform rotate-[-5deg]"
                        >
                            <span className="text-sm md:text-base font-bold tracking-[0.2em] uppercase">
                                {t('common.soldOut')}
                            </span>
                        </motion.div>
                    </div>
                )}

                {/* Overlay Gradient */}
                {/* Overlay Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 pointer-events-none ${isActive ? 'opacity-100' : 'opacity-0'}`}></div>

                <div className="absolute top-4 left-4 flex flex-col gap-2 rtl:left-auto rtl:right-4 z-10">
                    {badges && badges.length > 0 && badges.map((badge: string) => (
                        <span key={badge} className="bg-white/90 backdrop-blur-md text-primary text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
                            {badge}
                        </span>
                    ))}
                </div>

                {/* Discount Badge - Circular & Opposite Side */}
                {discountPrice && (
                    <div className="absolute top-4 right-4 rtl:right-auto rtl:left-4 z-10">
                        <div className="relative w-12 h-12 bg-accent rounded-full flex items-center justify-center shadow-lg transform rotate-12 transition-transform hover:rotate-0 hover:scale-110">
                            <div className="text-center leading-none text-white">
                                <span className="block text-xs font-bold">
                                    {Math.round(((price - discountPrice) / price) * 100)}%
                                </span>
                                <span className="block text-[8px] uppercase font-medium opacity-90">
                                    {isRTL ? 'خصم' : 'OFF'}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Quick Actions Overlay */}
                <AnimatePresence>
                    {isActive && !isSoldOut && (
                        <motion.div
                            className="absolute bottom-4 left-0 w-full px-4 flex gap-2 z-20"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 20, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <button
                                onClick={() => onQuickOrder(product)}
                                className="flex-1 bg-white text-primary font-bold py-3 rounded-xl hover:bg-primary hover:text-white transition-colors shadow-lg flex items-center justify-center gap-2 text-sm"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                {t('common.quickOrder')}
                            </button>
                            <button
                                onClick={() => addToCart(product)}
                                className="w-12 bg-accent text-white font-bold py-3 rounded-xl hover:bg-accent-dark transition-colors shadow-lg flex items-center justify-center"
                                title={t('common.addToCart')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Content */}
            <div className={`p-5 flex flex-col flex-grow relative bg-white ${isList ? 'h-full justify-center' : ''}`}>
                <div className="mb-2">
                    <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                        {isRTL
                            ? (product.categories?.[0]?.name_ar || product.category?.name_ar)
                            : (product.categories?.[0]?.name_fr || product.category?.name_fr)}
                    </span>
                </div>
                <Link to={`/product/${product.slug}`} className="block mb-2">
                    <h3 className={`font-bold text-dark transition-colors leading-tight ${isActive ? 'text-primary' : ''} ${isList ? 'text-sm md:text-2xl mb-2 md:mb-4' : 'text-lg line-clamp-2'}`}>
                        {title}
                    </h3>
                </Link>

                {/* Tags Display */}
                {product.tags && product.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                        {product.tags.slice(0, 3).map(tag => (
                            <span
                                key={tag.slug}
                                className="text-[10px] bg-secondary/10 text-secondary px-2 py-0.5 rounded-full font-bold"
                            >
                                {isRTL ? tag.name_ar : tag.name_fr}
                            </span>
                        ))}
                        {product.tags.length > 3 && (
                            <span className="text-[10px] text-gray-400">+{product.tags.length - 3}</span>
                        )}
                    </div>
                )}

                {/* Description helper for list view */}
                {isList && (
                    <p className="text-gray-500 mb-2 md:mb-4 line-clamp-2 text-xs md:text-sm">
                        {(isRTL ? product.short_desc_ar : product.short_desc_fr).replace(/<[^>]*>?/gm, '')}
                    </p>
                )}

                {/* Stock Progress Bar */}
                {isLowStock && (
                    <div className="mb-3">
                        <div className="flex justify-between text-[10px] font-bold text-orange-500 mb-1">
                            <span>{t('common.onlyLeft', { count: stock })}</span>
                            <span>{Math.round((stock / 20) * 100)}%</span>
                        </div>
                        <div className="w-full bg-orange-100 rounded-full h-1.5">
                            <div
                                className="bg-orange-500 h-1.5 rounded-full transition-all duration-1000"
                                style={{ width: `${(stock / 20) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                )}

                <div className={`mt-auto pt-2 flex items-center justify-between ${isList ? 'mt-4' : ''}`}>
                    <div className="flex flex-col">
                        {discountPrice ? (
                            <div className="flex items-baseline gap-2">
                                <span className="text-xl font-bold text-primary">
                                    {formatPrice(discountPrice, product.currency, i18n.language === 'ar' ? 'ar-MA' : 'fr-MA')}
                                </span>
                                <span className="text-sm text-gray-400 line-through decoration-red-400">
                                    {formatPrice(price, product.currency, i18n.language === 'ar' ? 'ar-MA' : 'fr-MA')}
                                </span>
                            </div>
                        ) : (
                            <span className="text-xl font-bold text-primary">
                                {formatPrice(price, product.currency, i18n.language === 'ar' ? 'ar-MA' : 'fr-MA')}
                            </span>
                        )}
                        <span className="text-xs text-gray-400 font-medium mt-1">
                            {isRTL
                                ? (product.unit_name_ar || 'قياسي')
                                : (product.unit_name_fr || 'Standart')}
                        </span>
                    </div>

                    {/* Rating placeholder if needed, or just keep it clean */}
                    <div className="flex items-center gap-1">
                        <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="text-xs font-bold text-gray-600">4.8</span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default ProductCard;
